import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import MRTable from '../../components/MRTable/MRTable';

import { getAllFeedback } from '../../../http/kyrpAPI';
import { IMPORTANCE_TYPES } from '../../../util/constants/AppConstants';
import { compareDates, formatUnixDate } from '../../../util/HelperFunctions';

import './KyrpDashboard.scss';

const KyrpDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);

  const config = useSelector((state) => state.config.config);

  const handleGetFeedbackData = async () => {
    setLoading(true);

    const res = await getAllFeedback(config);

    if (res.status === 200) {
      setFeedback(res.data.feedback);
      setLoading(false);
    } else {
      console.log('ERROR: Grabbing Feedback');
    }
  };

  useEffect(() => {
    if (config && config.api) {
      handleGetFeedbackData();
    }
  }, [config]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row) => {
          const { importance } = row;
          const option = IMPORTANCE_TYPES.find((option) => option.value === importance);
          return option ? option.label : '';
        },
        id: 'importance',
        header: 'Importance',
        minSize: 70,
        grow: 1,
        filterVariant: 'select',
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.importance;
          const b = rowB.original.importance;

          if (a === b) return 0;
          if (a === null || a === undefined) return 1;
          if (b === null || b === undefined) return -1;
          return a - b;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        minSize: 70,
        grow: 1,
        filterVariant: 'select',
      },
      {
        accessorFn: (row) => {
          const pollNumber = row.poll_label;
          return pollNumber ? `Poll - ${pollNumber}` : null;
        },
        id: 'poll_label',
        header: 'Poll',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
      },
      {
        accessorKey: 'riding',
        header: 'Riding',
        minSize: 70,
        grow: 1,
        filterVariant: 'select',
      },
      {
        accessorKey: 'type',
        header: 'Category',
        minSize: 150,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        minSize: 150,
        grow: 5,
        enableSorting: false,
      },
      {
        accessorFn: (row) => {
          const ai = row.created_by_ai;
          return ai ? 'AI' : 'Staffer';
        },
        id: 'created_by_ai',
        header: 'Origin',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'staffer_fullname',
        header: 'Added by',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'date_utc',
        header: 'Date',
        filterVariant: 'date',
        Cell: ({ cell }) => formatUnixDate(cell.getValue(), false),
        filterFn: (row, columnId, filterValue) => compareDates(new Date(row.getValue(columnId) * 1000), filterValue.$d),
        minSize: 150,
        grow: 1,
      },
    ];

    return baseColumns;
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="dashboard-components">
        <div className="dc-left full">
          <div className="sentiment-trend chart-component">
            <MRTable loading={loading} data={feedback} columns={columns} />
          </div>
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default KyrpDashboard;
